import type {
  Slot,
  OpeningHours as SpaceOpeningHours,
} from 'app/components/commons/Hotel/Types';
import type { Space } from 'app/redux/models/Space/Space';
import type { SpaceType } from 'app/redux/models/SpaceType/SpaceType';
import type {
  Opening,
  OpeningHours,
  TimeRange,
} from 'app/typings/core/admin/experiences/openingHours/openingHours';
import { OpeningHoursType } from 'app/typings/core/admin/experiences/openingHours/openingHours';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';
import { dayjs } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

export const DEFAULT_SLOT = { start: '09:00', end: '18:00' };

export type PresetOpeningHours = {
  name: string;
  openingHours: OpeningHours;
};

const TIME_PATTERN = /(\d{2}):(\d{2})/;

const toHourMinute = (time?: string | null): string | undefined => {
  const match = time?.match(TIME_PATTERN);

  return match ? `${match[1]}:${match[2]}` : undefined;
};

const toTimeRanges = (slots?: Slot[]): TimeRange[] =>
  (slots ?? []).flatMap((slot) => {
    const start = toHourMinute(slot.start);
    const end = toHourMinute(slot.end);

    return start && end ? [{ start, end }] : [];
  });

const toOpening = (opening: { isAllDay?: boolean; slots?: Slot[] }): Opening =>
  opening.isAllDay
    ? { isAllDay: true }
    : { isAllDay: false, slots: toTimeRanges(opening.slots) };

const hasOpenings = (opening: Opening): boolean =>
  opening.isAllDay || opening.slots.length > 0;

export const normalizeOpeningHours = (
  openingHours?: Partial<SpaceOpeningHours> | null
): OpeningHours | undefined => {
  if (!openingHours) {
    return undefined;
  }

  if (openingHours.type === OpeningHoursType.DAILY) {
    const days = Object.fromEntries(
      Object.entries(openingHours.days ?? {})
        .map(([day, opening]) => [day, toOpening(opening)] as const)
        .filter(([, opening]) => hasOpenings(opening))
    ) as Partial<Record<DayOfWeek, Opening>>;

    return Object.keys(days).length > 0
      ? { type: OpeningHoursType.DAILY, days }
      : undefined;
  }

  const opening = toOpening(openingHours);

  return hasOpenings(opening)
    ? { type: OpeningHoursType.WEEKLY, ...opening }
    : undefined;
};

export const presetOpeningHours = (
  spaces?: Space[],
  spaceIds?: number[],
  spaceTypes?: SpaceType[]
): PresetOpeningHours[] => {
  const getName = (space: Space) =>
    space.name && space.name.trim().length > 0
      ? space.name
      : spaceTypes?.find((s) => s.id === space.spaceTypeId)?.name;

  return (spaces ?? [])
    .filter((space) => space.id && spaceIds?.includes(space.id))
    .flatMap((space) => {
      const name = getName(space);
      const openingHours = normalizeOpeningHours(space.openingHours);

      return name && openingHours ? [{ name, openingHours }] : [];
    });
};

export const isSlotEndAfterStart = (
  slot?: { start?: string; end?: string } | null
): boolean => {
  const start = toHourMinute(slot?.start);
  const end = toHourMinute(slot?.end);

  return !start || !end || end > start;
};

export const formatToDayjs = (slot: { start: string; end: string }) => [
  dayjs(slot.start, 'HH:mm'),
  dayjs(slot.end, 'HH:mm'),
];

export const formatDayjsToTimeRange = (
  dayjsRange: [Dayjs, Dayjs] | null
): { start: string; end: string } | null => {
  if (!dayjsRange) {
    return null;
  }

  return {
    start: dayjsRange[0].format('HH:mm'),
    end: dayjsRange[1].format('HH:mm'),
  };
};
